// FirestoreContext.tsx
import React, { createContext, useContext } from "react";
import { Invoice } from "../interfaces/invoice";
import { InventoryItem } from "../interfaces/inventoryItem";
import { Venue } from "../interfaces/venue";
import { TaxRate } from "../interfaces/taxRate";

type FirestoreContextType = {
  invoicesData: {
    todaysInvoices: Invoice[];
    yesterdayInvoices: Invoice[];
    tomorrowInvoices: Invoice[];
    lastWeekInvoices: Invoice[];
    nextWeekInvoices: Invoice[];
  };
  inventory: InventoryItem[];
  loadingInventory: boolean;
  users: any[];
  tags: any[];
  categories: any[];
  venues: Venue[];
  taxes: TaxRate[];
  files: any[];
  invoiceTextData: any;
  emailInvoiceSettings: any;
  emailEstimateSettings: any;
  initialInventoryLoaded: boolean;
  // Add functions
  addInventoryItem: (newItem: InventoryItem) => void;
  addUser: (newUser: any) => void;
  addTag: (newTag: any) => void;
  addCategory: (newCategory: any) => void;
  addVenue: (newVenue: Venue) => void;
  addTax: (newTax: TaxRate) => void;
  addFile: (newFile: any) => void;
  addInvoice: (newInvoice: Invoice, listType: keyof FirestoreContextType['invoicesData']) => void;
  
  // Update functions
  updateInventoryItem: (id: string, updatedItem: Partial<InventoryItem>) => void;
  updateUser: (id: string, updatedUser: Partial<any>) => void;
  updateTag: (id: string, updatedTag: Partial<any>) => void;
  updateCategory: (id: string, updatedCategory: Partial<any>) => void;
  updateVenue: (id: string, updatedVenue: Partial<Venue>) => void;
  updateTax: (id: string, updatedTax: Partial<TaxRate>) => void;
  updateFile: (id: string, updatedFile: Partial<any>) => void;
  updateInvoice: (id: string, updatedInvoice: Partial<Invoice>, listType: keyof FirestoreContextType['invoicesData']) => void;
  updateInvoiceTextData: (data: any) => void;
  updateEmailInvoiceSettings: (settings: any) => void;
  updateEmailEstimateSettings: (settings: any) => void;
}

// Define the FirestoreContext
export const FirestoreInvoicesContext = createContext<FirestoreContextType>({
  invoicesData: {
    todaysInvoices: [],
    yesterdayInvoices: [],
    tomorrowInvoices: [],
    lastWeekInvoices: [],
    nextWeekInvoices: [],
  },
  inventory: [],
  loadingInventory: true,
  users: [],
  tags: [],
  categories: [],
  venues: [],
  taxes: [],
  files: [],
  invoiceTextData: null,
  emailInvoiceSettings: null,
  emailEstimateSettings: null,
  initialInventoryLoaded: false,
  
  // Default empty functions
  addInventoryItem: () => {},
  addUser: () => {},
  addTag: () => {},
  addCategory: () => {},
  addVenue: () => {},
  addTax: () => {},
  addFile: () => {},
  addInvoice: () => {},
  
  updateInventoryItem: () => {},
  updateUser: () => {},
  updateTag: () => {},
  updateCategory: () => {},
  updateVenue: () => {},
  updateTax: () => {},
  updateFile: () => {},
  updateInvoice: () => {},
  updateInvoiceTextData: () => {},
  updateEmailInvoiceSettings: () => {},
  updateEmailEstimateSettings: () => {},
});

// Export the FirestoreContext
export const useFirestoreContext = () => {
  const context = useContext(FirestoreInvoicesContext);

  if (!context)
    throw new Error("useFirestoreContext must be use inside FirestoreProvider");

  return context;
};

// Create a provider component
export const FirestoreProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = React.useState<Omit<FirestoreContextType, 
    'addInventoryItem' | 'addUser' | 'addTag' | 'addCategory' | 'addVenue' | 'addTax' | 'addFile' | 'addInvoice' |
    'updateInventoryItem' | 'updateUser' | 'updateTag' | 'updateCategory' | 'updateVenue' | 'updateTax' | 
    'updateFile' | 'updateInvoice' | 'updateInvoiceTextData' | 'updateEmailInvoiceSettings' | 'updateEmailEstimateSettings'>>({
    invoicesData: {
      todaysInvoices: [],
      yesterdayInvoices: [],
      tomorrowInvoices: [],
      lastWeekInvoices: [],
      nextWeekInvoices: [],
    },
    inventory: [],
    loadingInventory: true,
    users: [],
    tags: [],
    categories: [],
    venues: [],
    taxes: [],
    files: [],
    invoiceTextData: null,
    emailInvoiceSettings: null,
    emailEstimateSettings: null,
    initialInventoryLoaded: false,
  });

  // Add functions
  const addInventoryItem = React.useCallback((newItem: InventoryItem) => {
    setState(prevState => ({
      ...prevState,
      inventory: [...prevState.inventory, newItem]
    }));
  }, []);

  const addUser = React.useCallback((newUser: any) => {
    setState(prevState => ({
      ...prevState,
      users: [...prevState.users, newUser]
    }));
  }, []);

  const addTag = React.useCallback((newTag: any) => {
    setState(prevState => ({
      ...prevState,
      tags: [...prevState.tags, newTag]
    }));
  }, []);

  const addCategory = React.useCallback((newCategory: any) => {
    setState(prevState => ({
      ...prevState,
      categories: [...prevState.categories, newCategory]
    }));
  }, []);

  const addVenue = React.useCallback((newVenue: Venue) => {
    setState(prevState => ({
      ...prevState,
      venues: [...prevState.venues, newVenue]
    }));
  }, []);

  const addTax = React.useCallback((newTax: TaxRate) => {
    setState(prevState => ({
      ...prevState,
      taxes: [...prevState.taxes, newTax]
    }));
  }, []);

  const addFile = React.useCallback((newFile: any) => {
    setState(prevState => ({
      ...prevState,
      files: [...prevState.files, newFile]
    }));
  }, []);

  const addInvoice = React.useCallback((newInvoice: Invoice, listType: keyof FirestoreContextType['invoicesData']) => {
    setState(prevState => ({
      ...prevState,
      invoicesData: {
        ...prevState.invoicesData,
        [listType]: [...prevState.invoicesData[listType], newInvoice]
      }
    }));
  }, []);

  // Update functions
  const updateInventoryItem = React.useCallback((id: string, updatedItem: Partial<InventoryItem>) => {
    setState(prevState => ({
      ...prevState,
      inventory: prevState.inventory.map(item => 
        item.id === id ? { ...item, ...updatedItem } : item
      )
    }));
  }, []);

  const updateUser = React.useCallback((id: string, updatedUser: Partial<any>) => {
    setState(prevState => ({
      ...prevState,
      users: prevState.users.map(user => 
        user.id === id ? { ...user, ...updatedUser } : user
      )
    }));
  }, []);

  const updateTag = React.useCallback((id: string, updatedTag: Partial<any>) => {
    setState(prevState => ({
      ...prevState,
      tags: prevState.tags.map(tag => 
        tag.id === id ? { ...tag, ...updatedTag } : tag
      )
    }));
  }, []);

  const updateCategory = React.useCallback((id: string, updatedCategory: Partial<any>) => {
    setState(prevState => ({
      ...prevState,
      categories: prevState.categories.map(category => 
        category.id === id ? { ...category, ...updatedCategory } : category
      )
    }));
  }, []);

  const updateVenue = React.useCallback((id: string, updatedVenue: Partial<Venue>) => {
    setState(prevState => ({
      ...prevState,
      venues: prevState.venues.map(venue => 
        venue.id === id ? { ...venue, ...updatedVenue } : venue
      )
    }));
  }, []);

  const updateTax = React.useCallback((id: string, updatedTax: Partial<TaxRate>) => {
    setState(prevState => ({
      ...prevState,
      taxes: prevState.taxes.map(tax => 
        tax.id === id ? { ...tax, ...updatedTax } : tax
      )
    }));
  }, []);

  const updateFile = React.useCallback((id: string, updatedFile: Partial<any>) => {
    setState(prevState => ({
      ...prevState,
      files: prevState.files.map(file => 
        file.id === id ? { ...file, ...updatedFile } : file
      )
    }));
  }, []);

  const updateInvoice = React.useCallback((id: string, updatedInvoice: Partial<Invoice>, listType: keyof FirestoreContextType['invoicesData']) => {
    setState(prevState => ({
      ...prevState,
      invoicesData: {
        ...prevState.invoicesData,
        [listType]: prevState.invoicesData[listType].map(invoice => 
          invoice.id === id ? { ...invoice, ...updatedInvoice } : invoice
        )
      }
    }));
  }, []);

  const updateInvoiceTextData = React.useCallback((data: any) => {
    setState(prevState => ({
      ...prevState,
      invoiceTextData: data
    }));
  }, []);

  const updateEmailInvoiceSettings = React.useCallback((settings: any) => {
    setState(prevState => ({
      ...prevState,
      emailInvoiceSettings: settings
    }));
  }, []);

  const updateEmailEstimateSettings = React.useCallback((settings: any) => {
    setState(prevState => ({
      ...prevState,
      emailEstimateSettings: settings
    }));
  }, []);

  return (
    <FirestoreInvoicesContext.Provider 
      value={{ 
        ...state, 
        addInventoryItem,
        addUser,
        addTag,
        addCategory,
        addVenue,
        addTax,
        addFile,
        addInvoice,
        updateInventoryItem,
        updateUser,
        updateTag,
        updateCategory,
        updateVenue,
        updateTax,
        updateFile,
        updateInvoice,
        updateInvoiceTextData,
        updateEmailInvoiceSettings,
        updateEmailEstimateSettings,
      }}
    >
      {children}
    </FirestoreInvoicesContext.Provider>
  );
};
